import React from 'react';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      <div className="about-page-shell">
        <div className="about-hero">
          <div className="about-hero-inner">
            <p className="about-eyebrow">About us</p>
            <h1>Xenvota Tech Solutions</h1>
            <p className="about-lead">
              Xenvota Tech Solutions is a team of experienced mobile and full-stack engineers who build
              secure, maintainable, and scalable digital products. We specialize in cross-platform apps,
              backend services, and full-cycle delivery with proven results in enterprise-grade
              projects.
            </p>
            <p className="about-lead-sub">
              We care about tools that are simple to pick up, upfront about what they do, and ready to
              grow when you do. Automation, machine learning, and solid engineering are how we cut
              repetitive work and help you get clearer, better results from ordinary days.
            </p>
          </div>
        </div>

        <div className="about-flow">
          <section className="about-chapter">
            <h2>Our mission</h2>
            <p>
              We want advanced technology to feel approachable—not locked behind jargon or complex
              setups. That means products with clear value, sensible defaults, and documentation and
              support that respect your time.
            </p>
            <p>
              Every release we ship is measured against a simple question: does this materially help
              someone do their job, reach their audience, or preserve something they care about? If
              the answer is no, we keep iterating.
            </p>
            <p className="about-quiet">In practice, our mission shows up as tools that help you:</p>
            <ul className="about-list-relaxed">
              <li>
                Save time by automating repetitive checks and workflows—without giving up control or
                visibility
              </li>
              <li>
                Improve efficiency across marketing, operations, and creative work with reliable,
                cloud-based software
              </li>
              <li>
                Unlock real value from AI where it genuinely fits: augmentation and quality gains, not
                buzzword features
              </li>
            </ul>
          </section>

          <section className="about-chapter">
            <h2>What we do</h2>
            <div className="about-pillars">
              <div className="about-pillar">
                <h3>AI-powered tools</h3>
                <p>
                  We build intelligent features where they remove friction: validation, enhancement,
                  and assistive workflows. Models and heuristics are chosen for accuracy and speed,
                  then wrapped in interfaces that do not require a data science background to use
                  well.
                </p>
              </div>
              <div className="about-pillar">
                <h3>SaaS products</h3>
                <p>
                  Our software is delivered as a service—updated continuously, monitored for uptime,
                  and structured so you can start small and expand usage as your needs grow.
                  Security, privacy, and clear pricing belong in the same conversation as features.
                </p>
              </div>
              <div className="about-pillar">
                <h3>Custom solutions</h3>
                <p>
                  When off-the-shelf is not enough, we work with organizations to design integrations,
                  internal tools, and product extensions. Engagements are scoped around outcomes,
                  milestones, and maintainability—not open-ended builds without an owner.
                </p>
              </div>
            </div>
          </section>

          <section className="about-chapter about-chapter-products">
            <h2>Our products</h2>
            <div className="about-product-cards">
              <div className="about-product-card">
                <h3>Email Validify</h3>
                <p>
                  Email Validify helps teams and marketers protect sender reputation and campaign ROI.
                  Invalid or risky addresses inflate costs, skew reporting, and can hurt deliverability
                  for your entire domain. The platform is built for quick single checks as well as
                  larger list hygiene workflows, so you can validate at the pace your business moves.
                </p>
                <ul className="about-list-relaxed">
                  <li>
                    <strong>Real-Time Verification</strong> — validate addresses as they are captured
                    or typed, ideal for signup forms and CRM entry points
                  </li>
                  <li>
                    <strong>Bulk Verification</strong> — process large files or exports so you can
                    clean legacy lists before major sends or migrations
                  </li>
                  <li>
                    <strong>Syntax and Format Checking</strong> — catch malformed addresses early,
                    before they enter your database or automation flows
                  </li>
                </ul>
                <p className="about-product-card-link">
                  Visit:{' '}
                  <a href="https://emailvalidify.com/" rel="noopener noreferrer" target="_blank">
                    emailvalidify.com
                  </a>
                </p>
              </div>
              <div className="about-product-card">
                <h3>Enhanzio</h3>
                <p>
                  <strong>Enhanzio AI — Photo Enhancer</strong> focuses on one job: making photos look
                  their best. Old scans, compressed social images, and soft captures benefit from
                  modern upscaling and restoration, presented through a straightforward flow so you
                  spend time on the story—not on knobs and sliders you do not need.
                </p>
                <ul className="about-list-relaxed">
                  <li>
                    Enhance perceived quality using AI-driven detail and noise-aware processing tuned
                    for portraits, documents, and general scenes
                  </li>
                  <li>
                    Improve clarity and effective resolution so images hold up when printed, cropped,
                    or displayed on high-DPI screens
                  </li>
                  <li>
                    Restore and refine visuals with minimal friction: upload, preview, export—without
                    a heavyweight editing suite
                  </li>
                </ul>
                <p className="about-product-card-link">
                  Visit:{' '}
                  <a href="http://enhanzio.xenvota.com/" rel="noopener noreferrer" target="_blank">
                    enhanzio.xenvota.com
                  </a>
                </p>
              </div>
            </div>
            <p className="about-soft-close">
              Email Validify and Enhanzio sit in different categories, but they share the same product
              philosophy: a calm surface for the user, disciplined engineering underneath, and respect
              for data whether it is an inbox list or a personal photo.
            </p>
          </section>

          <section className="about-chapter">
            <h2>Our business model</h2>
            <p>
              We stay flexible so customers can enter at the level that matches their budget and
              stage. Many workflows start with a free tier or trial; others move quickly into paid
              capacity when volume, features, or support requirements grow.
            </p>
            <p>A flexible digital model that meets different needs:</p>
            <ul className="about-list-relaxed">
              <li>
                <strong>Freemium access</strong> — explore core features without a long commitment,
                so you can validate fit before you scale usage
              </li>
              <li>
                <strong>Paid plans</strong> — unlock higher limits, advanced capabilities, or priority
                paths via subscription or one-time purchase, depending on the product
              </li>
              <li>
                <strong>Services</strong> — when you need implementation help, integrations, or a
                bespoke build, we scope work with clear milestones rather than open-ended engagements
              </li>
            </ul>
            <p className="about-note">
              Payments are processed through established providers, with attention to security and
              compliance appropriate to the product. Product-specific terms describe refunds, billing,
              and data handling where they differ from standard policies.
            </p>
          </section>

          <section className="about-chapter">
            <h2>How we operate</h2>
            <p>
              We run as a distributed, digital-first company. That keeps overhead lean and puts
              investment into product quality, infrastructure, and the people who build and support
              it.
            </p>
            <ul className="about-list-relaxed">
              <li>
                <strong>Digital delivery</strong> — onboarding, documentation, and account management
                are designed for online access, so you can get started without shipping hardware or
                waiting on paper processes
              </li>
              <li>
                <strong>Support</strong> — questions and issues are handled through email and other
                online channels; we aim for responses that are clear and actionable, not generic
                templates
              </li>
              <li>
                <strong>Continuous improvement</strong> — we ship updates regularly, whether that is
                performance tuning, new features, or fixes informed by real usage
              </li>
              <li>
                <strong>Performance and scale</strong> — latency, error rates, and capacity planning
                are treated as product issues, not afterthoughts, especially for verification and media
                workloads
              </li>
            </ul>
          </section>

          <section className="about-chapter">
            <h2>Our commitment</h2>
            <p>
              Trust is easier to lose than to earn. These principles guide how we market our products,
              handle data, and behave when something goes wrong.
            </p>
            <ul className="about-list-relaxed">
              <li>
                <strong>Transparency</strong> — we describe what our tools do in plain language and
                surface limitations where they matter, so you can decide fit without decoding
                marketing fluff
              </li>
              <li>
                <strong>Privacy and data respect</strong> — we collect what we need to run the service,
                protect it with industry-standard practices, and document how retention and rights work
                in our policies
              </li>
              <li>
                <strong>Reliability</strong> — we invest in monitoring, testing, and sensible rollouts
                so upgrades improve the experience more often than they disrupt it
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
